/**
 * Private Zones — owner-scoped infrastructure view (hosts, datastores, networks,
 * capacity) for zones the signed-in user owns. Ported from the legacy @console
 * `PrivateZone` module.
 *
 * Unlike the compute connectors these endpoints are NOT service-scoped: they sit
 * directly under the v2 gateway (`serviceApi` base already includes `/api`):
 *   GET /v2/zones/owned            → ownership flag + owned-zone list
 *   GET /v2/zones/:id/resources    → capacity + hosts + datastores + networks
 *   GET /v2/zones/:id              → zone summary
 * `:id/resources` returns 403 when the caller is not the zone owner, 404 when the
 * zone is missing — neither is retried.
 */
import { useQuery } from '@tanstack/react-query';
import { serviceApi } from '@/api/client';
import { unwrap } from './_shared';

// ---- Types ---------------------------------------------------------------

export interface OwnedZone {
  id: string;
  name: string;
  provider_scope?: string;
}

export interface OwnershipResponse {
  has_private_zone: boolean;
  owned_zones: OwnedZone[];
}

export interface CapacityValue {
  total: number;
  used: number;
  unit?: string;
}

export interface ZoneCapacity {
  cpu?: CapacityValue;
  memory?: CapacityValue;
  datastore?: CapacityValue;
  backup?: CapacityValue;
}

export interface ZoneHost {
  id: string;
  name: string;
  state: string;
  cluster_id?: string;
  cluster_name?: string;
  vm_count?: number;
  cpu_total?: number;
  cpu_used?: number;
  mem_total?: number;
  mem_used?: number;
}

export interface ZoneDatastore {
  id: string;
  name: string;
  type?: string;
  cluster_ids?: number[];
  total_mb?: number;
  used_mb?: number;
  free_mb?: number;
}

export interface ZoneCluster {
  id: string;
  name: string;
  host_ids: string[];
  datastore_ids: string[];
  vnet_ids: string[];
}

export interface ZoneNetwork {
  id: string;
  name: string;
  vlan_id?: number | string;
  cluster_ids?: number[];
  used_leases?: number;
  total_ips?: number;
}

export interface ZoneResources {
  zone: { id: string; name: string };
  capacity: ZoneCapacity;
  hosts: ZoneHost[];
  clusters: ZoneCluster[];
  datastores: ZoneDatastore[];
  networks: ZoneNetwork[];
  error?: string;
}

// ---- Query keys ----------------------------------------------------------

export const zoneKeys = {
  owned: ['zones', 'owned'] as const,
  resources: (id: string) => ['zones', id, 'resources'] as const,
};

// ---- Hooks ---------------------------------------------------------------

/** Ownership flag + the list of zones the current user owns. */
export function useOwnedZones() {
  const query = useQuery({
    queryKey: zoneKeys.owned,
    staleTime: 60_000,
    retry: false,
    queryFn: async () => unwrap<OwnershipResponse>(await serviceApi.get('/v2/zones/owned')),
  });

  return {
    ...query,
    hasPrivateZone: query.data?.has_private_zone ?? false,
    ownedZones: query.data?.owned_zones ?? [],
  };
}

/** Resource dashboard for one zone. 401/403/404 are not retried. */
export function useZoneResources(id: string | undefined) {
  return useQuery({
    queryKey: zoneKeys.resources(id ?? ''),
    enabled: !!id,
    staleTime: 60_000,
    retry: (count, err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 403 || status === 404) return false;
      return count < 2;
    },
    queryFn: async () => unwrap<ZoneResources>(await serviceApi.get(`/v2/zones/${id}/resources`)),
  });
}
